import React, { useContext, useMemo } from 'react';
import { Grid, Card, CardHeader, CardContent, Box, Skeleton } from '@mui/material';
import { useOutpatientsData } from '../../hooks/useOutpatientsData';
import { outpatientChartsForMode, ChartConfig } from '../../config/analyticsCharts';
import ErrorState from '../../common/ErrorState';
import ChartFactory from '../../charts/ChartFactory';
import { IDepartment } from '../../../../../constants/types';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { AnalyticsViewMode } from '../../types';
import type { SourceState } from '../../types';
import { getCategoryColor } from '../../utils/categoryColors';
import { payerCategoryLabels } from '../../utils/payerCategoryLabels';

interface OutpatientsTabProps {
  investigation: {
    permissions: string[];
    [key: string]: any;
  };
  departmentSelected: string;
  startDate: number | null;
  endDate: number | null;
  departments: IDepartment[];
  mode: AnalyticsViewMode;
}

// This maps specific chart IDs to special handling functions
type ChartHandlerContext = { translate: (id: string) => any };

const specialChartHandlers: Record<string, (data: any, ctx: ChartHandlerContext) => any> = {
  // Handle appointment type charts specially to enforce consistent colors
  'appointmentsByType': (data) => {
    if (!data || !data.series) return data;
    
    // Explicitly assign colors to appointment types
    const processedSeries = data.series.map((s: any) => ({
      ...s,
      color: getCategoryColor(s.name) // Use our category color system
    }));
    
    return {
      ...data,
      series: processedSeries
    };
  },
  
  // Handle insurance charts specially
  'appointmentsByInsurance': (data, { translate }) => {
    if (!data || !data.series) return data;
    
    // Explicitly assign colors to insurance types
    const processedSeries = data.series.map((s: any) => ({
      ...s,
      color: getCategoryColor(s.name) // Use our category color system
    }));
    
    // SHER-900: a grouped card arrives as payer-category keys; the labeller is a no-op on the
    // per-insurer fallback, which carries no tag.
    return payerCategoryLabels({
      ...data,
      series: processedSeries
    }, translate);
  }
};

const OutpatientsTab: React.FC<OutpatientsTabProps> = ({ 
  investigation, 
  departmentSelected, 
  startDate, 
  endDate, 
  departments,
  mode,
}) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. Chart labels must be plain strings, so they cannot be <Translate/>.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const { data, sourceStates } = useOutpatientsData({
    investigation,
    departmentSelected,
    startDate: startDate?.toString() || '',
    endDate: endDate?.toString() || '',
    mode,
  });

  // SHER-562: the tier is part of the filter, not only of the fetch. The predicate lives in
  // the config module so the hook and this tab cannot disagree about what the mode contains.
  const chartsForMode = useMemo(() => outpatientChartsForMode(mode), [mode]);
  const visibleCharts = useMemo(() => chartsForMode.filter(chart => chart.visible), [chartsForMode]);
  const debugCharts = useMemo(() => chartsForMode.filter(chart => chart.debug), [chartsForMode]);

  const defaultDepartment = useMemo(() => 
    departments && departments.length > 0 ? departments[0] : null,
    [departments]
  );

  // Process chart data to ensure consistent colors
  const processChartData = (chartId: string, data: any) => {
    // Apply special handler if one exists for this chart
    const handler = specialChartHandlers[chartId];
    if (handler) {
      return handler(data, { translate: translate as (id: string) => any });
    }
    
    // Default processing - return unchanged
    return data;
  };

  // Each card waits for — and fails with — its own endpoint. The tab used to block on the
  // slowest of thirteen before drawing anything, and a single rejected request used to
  // replace every chart with one error panel.
  const renderCard = (chart: ChartConfig, state: SourceState | undefined) => {
    switch (state?.status) {
      case 'pending':
        return <Skeleton variant="rectangular" width="100%" height={300} />;
      case 'error':
        return <ErrorState error={state.error} />;
      default:
        // No state means the source belongs to the other tier — draw the chart, as before.
        return (
          <ChartFactory
            id={chart.id}
            type={chart.type}
            title={chart.title}
            data={processChartData(chart.id, (data as any)[chart.source])}
            departments={departments}
            departmentSelected={defaultDepartment}
            topN={chart.topN}
            payerCategories={chart.payerCategories}
          />
        );
    }
  };

  return (
    <>
    <Grid container spacing={3}>
      {visibleCharts.map((chart) => (
        <Grid 
          item 
          xs={12} 
          sm={chart.cols === 12 ? 12 : 6} 
          md={chart.cols} 
          key={chart.id}
        >
          {renderCard(chart, sourceStates[chart.source])}
        </Grid>
      ))} 
    </Grid>
      <>
      {debugCharts.map((chart) => (
        <Card key={chart.id}>
        <CardHeader title={chart.id} subheader={chart.source} />
        <CardContent>
          <Box >
            {(data as any)[chart.source] && (
              <pre style={{ 
                overflow: 'auto', 
                maxHeight: '100%',
                margin: 0,
                padding: '1rem',
                background: '#f5f5f5',
                borderRadius: '4px'
              }}>
                {JSON.stringify((data as any)[chart.source], null, 2)}
              </pre>
            )}
          </Box>
        </CardContent>
      </Card>
            ))}
      </>

    </>
  );
};

export default OutpatientsTab;
